import {
  getFlightRouteLabel,
  getFlightRouteParts,
  getFlightNumberLabel,
  isFlightItineraryItem,
  normalizeAirportCode,
  inferAirportFromLocation,
  preferLocationInferredCode,
  parseTransportationData,
  formatCityToCityLabel,
  getAirportRouteCity,
} from './flightRouteUtils';
import { normalizeDateTimeLocal } from './flightScheduleUtils';

const FLIGHT_NUMBER_PATTERN = /\b([A-Za-z]{1,3})\s*(\d{1,4})\b/;

export const parseFlightNumberInput = (input = '') => {
  const trimmed = String(input).trim();
  if (!trimmed) {
    return null;
  }

  const compactMatch = trimmed.match(/^([A-Za-z]{2,3})(\d{1,4})$/i);
  if (compactMatch) {
    return {
      airline: compactMatch[1].toUpperCase(),
      flight_number: compactMatch[2],
    };
  }

  const spacedMatch = trimmed.match(FLIGHT_NUMBER_PATTERN);
  if (spacedMatch) {
    return {
      airline: spacedMatch[1].toUpperCase(),
      flight_number: spacedMatch[2],
    };
  }

  return null;
};

const readField = (data, ...keys) => {
  for (const key of keys) {
    if (data?.[key]) {
      return data[key];
    }
  }
  return null;
};

export const getFlightLegFromItem = (item) => {
  const data = parseTransportationData(item?.transportation_data);
  const route = getFlightRouteParts(item);
  const parsedNumber =
    parseFlightNumberInput(readField(data, 'flight_number', 'flightNumber')) ||
    parseFlightNumberInput(getFlightNumberLabel(item)) ||
    parseFlightNumberInput(item?.name);

  const descriptionFrom = item?.description?.match(/From:\s*(.+)/i)?.[1]?.trim();
  const descriptionTo = item?.description?.match(/To:\s*(.+)/i)?.[1]?.trim();

  return {
    id: item?.id,
    airline: readField(data, 'airline') || parsedNumber?.airline || '',
    airline_code: readField(data, 'airline_code', 'airlineCode') || parsedNumber?.airline || '',
    flight_number: readField(data, 'flight_number', 'flightNumber') || parsedNumber?.flight_number || '',
    departure_airport:
      route?.from ||
      normalizeAirportCode(readField(data, 'departure_airport', 'departureAirport', 'origin', 'origin_airport')) ||
      inferAirportFromLocation(descriptionFrom) ||
      inferAirportFromLocation(item?.location),
    arrival_airport:
      route?.to ||
      normalizeAirportCode(readField(data, 'arrival_airport', 'arrivalAirport', 'destination', 'destination_airport')) ||
      inferAirportFromLocation(descriptionTo),
    departure_time: readField(data, 'departure_time', 'departureTime') || item?.start_datetime || '',
    arrival_time: readField(data, 'arrival_time', 'arrivalTime') || item?.end_datetime || '',
    confirmation_number: readField(data, 'confirmation_number', 'confirmationNumber', 'booking_reference'),
    terminal: readField(data, 'terminal'),
    gate: readField(data, 'gate'),
    seat: readField(data, 'seat'),
    sourceItem: item,
  };
};

export const getFlightLegsFromItem = (item) => {
  const storedLegs = parseTransportationData(item?.transportation_data).legs;
  if (Array.isArray(storedLegs) && storedLegs.length > 0) {
    return storedLegs.map((leg, index) => ({
      ...leg,
      airline: leg.airline || '',
      airline_code: leg.airline_code || leg.airlineCode || '',
      flight_number: leg.flight_number || leg.flightNumber || '',
      departure_airport: normalizeAirportCode(
        leg.departure_airport || leg.from_airport || leg.origin || leg.origin_airport
      ) || '',
      arrival_airport: normalizeAirportCode(
        leg.arrival_airport || leg.to_airport || leg.destination || leg.destination_airport
      ) || '',
      departure_time: leg.departure_time || leg.departureTime || '',
      arrival_time: leg.arrival_time || leg.arrivalTime || '',
      id: leg.id || `${item?.id || 'flight'}-leg-${index}`,
    }));
  }

  return [getFlightLegFromItem(item)];
};

const readLegAirport = (leg, type) => {
  if (!leg) {
    return '';
  }

  const keys = type === 'departure'
    ? ['departure_airport', 'from_airport', 'origin', 'origin_airport']
    : ['arrival_airport', 'to_airport', 'destination', 'destination_airport'];

  for (const key of keys) {
    const code = normalizeAirportCode(leg[key]);
    if (code) {
      return code;
    }
  }

  return '';
};

export const enrichFlightLegForForm = (leg, item, index, allLegs) => {
  const route = getFlightRouteParts(item);
  let departureAirport = readLegAirport(leg, 'departure');
  let arrivalAirport = readLegAirport(leg, 'arrival');

  if (!departureAirport && index === 0) {
    departureAirport = route?.from || inferAirportFromLocation(item?.location) || '';
  }

  if (!departureAirport && index > 0) {
    departureAirport = readLegAirport(allLegs[index - 1], 'arrival');
  }

  if (!arrivalAirport && index < allLegs.length - 1) {
    arrivalAirport = readLegAirport(allLegs[index + 1], 'departure');
  }

  if (!arrivalAirport && index === allLegs.length - 1) {
    const routeTo = route?.to || '';
    if (routeTo && routeTo !== departureAirport) {
      arrivalAirport = routeTo;
    }
  }

  const itemStart = item?.start_datetime || item?.transportation_data?.departure_time || '';
  const itemEnd = item?.end_datetime || item?.transportation_data?.arrival_time || '';

  let departureTime = leg.departure_time || '';
  let arrivalTime = leg.arrival_time || '';

  if (index === 0) {
    departureTime = normalizeDateTimeLocal(departureTime, itemStart);
  } else {
    departureTime = normalizeDateTimeLocal(departureTime);
  }

  if (index === allLegs.length - 1) {
    arrivalTime = normalizeDateTimeLocal(arrivalTime, itemEnd);
  } else {
    arrivalTime = normalizeDateTimeLocal(arrivalTime);
  }

  return {
    airline: leg.airline || '',
    airline_code: leg.airline_code || leg.airlineCode || '',
    flight_number: leg.flight_number || '',
    departure_airport: departureAirport,
    arrival_airport: arrivalAirport,
    departure_time: departureTime,
    arrival_time: arrivalTime,
  };
};

const getConfirmationKey = (item) => {
  const data = item?.transportation_data || {};
  return (
    readField(data, 'confirmation_number', 'confirmationNumber', 'booking_reference') || ''
  ).toString().trim().toUpperCase();
};

const legsConnect = (previousLeg, nextLeg) => {
  if (!previousLeg?.arrival_airport || !nextLeg?.departure_airport) {
    return false;
  }

  return previousLeg.arrival_airport === nextLeg.departure_airport;
};

// Two flights only belong to the same journey when the connection is a real
// layover, not a return flight days later. A round trip that departs and
// returns through the same hub must stay as two separate journeys.
const MAX_LAYOVER_MS = 24 * 60 * 60 * 1000;

const flightsConnectInTime = (previousItem, previousLeg, nextItem, nextLeg) => {
  const previousArrival = previousItem?.end_datetime || previousLeg?.arrival_time || null;
  const nextDeparture = nextItem?.start_datetime || nextLeg?.departure_time || null;
  if (!previousArrival || !nextDeparture) {
    // Fall back to airport-only matching when timing data is missing.
    return true;
  }
  const gap = new Date(nextDeparture).getTime() - new Date(previousArrival).getTime();
  if (Number.isNaN(gap)) {
    return true;
  }
  return gap >= 0 && gap <= MAX_LAYOVER_MS;
};

const buildJourneyFromItems = (items) => {
  const legs = items.flatMap((item) => getFlightLegsFromItem(item));
  const sortedItems = [...items].sort((a, b) => {
    const timeA = a.start_datetime ? new Date(a.start_datetime).getTime() : 0;
    const timeB = b.start_datetime ? new Date(b.start_datetime).getTime() : 0;
    return timeA - timeB;
  });

  return {
    id: items.map((item) => item.id).join('-'),
    items: sortedItems,
    legs,
    confirmationNumber: getConfirmationKey(items[0]) || null,
    start_datetime: sortedItems[0]?.start_datetime || legs[0]?.departure_time || null,
    end_datetime:
      sortedItems[sortedItems.length - 1]?.end_datetime ||
      legs[legs.length - 1]?.arrival_time ||
      null,
  };
};

export const groupFlightsIntoJourneys = (flightItems = []) => {
  if (!flightItems.length) {
    return [];
  }

  const sortedItems = [...flightItems].sort((a, b) => {
    const timeA = a.start_datetime ? new Date(a.start_datetime).getTime() : 0;
    const timeB = b.start_datetime ? new Date(b.start_datetime).getTime() : 0;
    if (timeA === 0) return 1;
    if (timeB === 0) return -1;
    return timeA - timeB;
  });

  const journeys = [];
  const usedIds = new Set();

  sortedItems.forEach((item) => {
    if (usedIds.has(item.id)) {
      return;
    }

    if (Array.isArray(item.transportation_data?.legs) && item.transportation_data.legs.length > 1) {
      journeys.push(buildJourneyFromItems([item]));
      usedIds.add(item.id);
    }
  });

  const remaining = sortedItems.filter((item) => !usedIds.has(item.id));
  const confirmationGroups = remaining.reduce((acc, item) => {
    const key = getConfirmationKey(item);
    if (!key) {
      return acc;
    }
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(item);
    return acc;
  }, {});

  // Same PNR can cover outbound + return days apart. Only merge legs that
  // actually connect as a layover; leave the rest for chaining / singles.
  Object.values(confirmationGroups).forEach((group) => {
    if (group.length <= 1) {
      return;
    }

    const sortedGroup = [...group].sort((a, b) => {
      const timeA = a.start_datetime ? new Date(a.start_datetime).getTime() : 0;
      const timeB = b.start_datetime ? new Date(b.start_datetime).getTime() : 0;
      return timeA - timeB;
    });

    let currentGroup = [sortedGroup[0]];
    for (let index = 1; index < sortedGroup.length; index += 1) {
      const nextItem = sortedGroup[index];
      const previousItem = currentGroup[currentGroup.length - 1];
      const previousLegs = getFlightLegsFromItem(previousItem);
      const nextLegs = getFlightLegsFromItem(nextItem);
      const previousLeg = previousLegs[previousLegs.length - 1];
      const nextLeg = nextLegs[0];

      if (
        legsConnect(previousLeg, nextLeg)
        && flightsConnectInTime(previousItem, previousLeg, nextItem, nextLeg)
      ) {
        currentGroup.push(nextItem);
        continue;
      }

      if (currentGroup.length > 1) {
        currentGroup.forEach((item) => usedIds.add(item.id));
        journeys.push(buildJourneyFromItems(currentGroup));
      }
      currentGroup = [nextItem];
    }

    if (currentGroup.length > 1) {
      currentGroup.forEach((item) => usedIds.add(item.id));
      journeys.push(buildJourneyFromItems(currentGroup));
    }
  });

  const chainCandidates = sortedItems.filter((item) => !usedIds.has(item.id));
  let currentGroup = [];

  chainCandidates.forEach((item) => {
    if (!currentGroup.length) {
      currentGroup = [item];
      return;
    }

    const previousItem = currentGroup[currentGroup.length - 1];
    const previousLegs = getFlightLegsFromItem(previousItem);
    const nextLegs = getFlightLegsFromItem(item);
    const previousLeg = previousLegs[previousLegs.length - 1];
    const nextLeg = nextLegs[0];

    if (
      legsConnect(previousLeg, nextLeg) &&
      flightsConnectInTime(previousItem, previousLeg, item, nextLeg)
    ) {
      currentGroup.push(item);
      return;
    }

    const previousTime = currentGroup[currentGroup.length - 1]?.start_datetime
      ? new Date(currentGroup[currentGroup.length - 1].start_datetime).getTime()
      : null;
    const nextTime = item.start_datetime ? new Date(item.start_datetime).getTime() : null;
    const withinLayoverWindow =
      previousTime &&
      nextTime &&
      nextTime >= previousTime &&
      nextTime - previousTime <= 48 * 60 * 60 * 1000;

    if (withinLayoverWindow && currentGroup.length < 4) {
      currentGroup.push(item);
      return;
    }

    if (currentGroup.length > 1) {
      currentGroup.forEach((groupItem) => usedIds.add(groupItem.id));
      journeys.push(buildJourneyFromItems(currentGroup));
    } else {
      usedIds.add(currentGroup[0].id);
      journeys.push(buildJourneyFromItems(currentGroup));
    }
    currentGroup = [item];
  });

  if (currentGroup.length) {
    if (currentGroup.length > 1) {
      currentGroup.forEach((groupItem) => usedIds.add(groupItem.id));
      journeys.push(buildJourneyFromItems(currentGroup));
    } else {
      usedIds.add(currentGroup[0].id);
      journeys.push(buildJourneyFromItems(currentGroup));
    }
  }

  return journeys.sort((a, b) => {
    const timeA = a.start_datetime ? new Date(a.start_datetime).getTime() : 0;
    const timeB = b.start_datetime ? new Date(b.start_datetime).getTime() : 0;
    return timeA - timeB;
  });
};

export const getEnrichedJourneyLegs = (journey, fallbackItem = null) => {
  const legs = journey?.legs || [];
  if (!legs.length) {
    return [];
  }

  const contextItem = {
    ...(fallbackItem || journey.items?.[0] || {}),
    name: fallbackItem?.name || journey.items?.[journey.items.length - 1]?.name || journey.items?.[0]?.name,
    start_datetime: journey.start_datetime || fallbackItem?.start_datetime,
    end_datetime: journey.end_datetime || fallbackItem?.end_datetime,
    location: fallbackItem?.location || journey.items?.[0]?.location,
    transportation_data: {
      ...(fallbackItem?.transportation_data || journey.items?.[0]?.transportation_data || {}),
      legs,
    },
  };

  return legs.map((leg, index) => enrichFlightLegForForm(leg, contextItem, index, legs));
};

export const getJourneyRouteLabel = (journey) => {
  const legs = journey?.legs || [];
  if (!legs.length) {
    return null;
  }

  const firstItem = journey.items?.[0];
  const lastItem = journey.items?.[journey.items.length - 1] || firstItem;

  if (journey.items?.length === 1) {
    const singleRoute = getFlightRouteLabel(journey.items[0]);
    if (singleRoute) {
      return singleRoute;
    }
  }

  const inferredOrigin = inferAirportFromLocation(firstItem?.location);
  const inferredDestination = inferAirportFromLocation(lastItem?.location);

  let from = preferLocationInferredCode(
    normalizeAirportCode(legs[0]?.departure_airport),
    inferredOrigin,
    firstItem?.location
  );
  let to = normalizeAirportCode(legs[legs.length - 1]?.arrival_airport) || inferredDestination;

  if (!to) {
    to = normalizeAirportCode(lastItem?.transportation_data?.arrival_airport) || inferredDestination;
  }

  if (from && to) {
    return from === to ? from : `${from} → ${to}`;
  }

  if (journey.items?.length === 1) {
    return getFlightRouteLabel(journey.items[0]);
  }

  return null;
};

export const getJourneyFlightNumbersLabel = (journey) => {
  const labels = (journey?.legs || [])
    .map((leg) => {
      const parsed = parseFlightNumberInput(leg.flight_number)
        || parseFlightNumberInput(`${leg.airline || ''}${leg.flight_number || ''}`.replace(/\s+/g, ''));
      if (parsed) {
        return `${parsed.airline}${parsed.flight_number}`;
      }
      return (leg.flight_number || '').trim() || null;
    })
    .filter(Boolean);

  return labels.length ? [...new Set(labels)].join(' · ') : null;
};

export const getJourneyCityRouteLabel = (journey) => {
  const first = journey?.items?.[0];
  const last = journey?.items?.[journey.items.length - 1] || first;
  const firstParts = first ? getFlightRouteParts(first) : null;
  const lastParts = last ? getFlightRouteParts(last) : null;
  const from = firstParts?.from || inferAirportFromLocation(first?.location);
  const to = lastParts?.to || firstParts?.to;
  return formatCityToCityLabel(from, to);
};

export const getJourneyLayoverLabel = (journey) => {
  const legs = journey?.legs || [];
  if (legs.length < 2) return null;
  const origin = normalizeAirportCode(legs[0]?.departure_airport);
  const destination = normalizeAirportCode(legs[legs.length - 1]?.arrival_airport);
  const viaCodes = [];
  legs.forEach((leg, index) => {
    if (index === legs.length - 1) return;
    const code = normalizeAirportCode(leg.arrival_airport) || normalizeAirportCode(legs[index + 1]?.departure_airport);
    if (code && code !== origin && code !== destination && !viaCodes.includes(code)) {
      viaCodes.push(code);
    }
  });
  if (!viaCodes.length) return null;
  return viaCodes.map((code) => getAirportRouteCity(code) || code).join(', ');
};

export const getJourneyReservationSubtitle = (journey) => {
  const numbers = getJourneyFlightNumbersLabel(journey);
  const first = journey?.items?.[0];
  const fallback = numbers || getFlightNumberLabel(first);
  if (!fallback) return null;
  const airlineName = (journey?.legs || [])
    .map((leg) => leg.airline)
    .find((airline) => airline && airline.trim().length > 3);
  const via = getJourneyLayoverLabel(journey);
  const withAirline = airlineName && !fallback.toLowerCase().startsWith(airlineName.toLowerCase())
    ? `${airlineName} ${fallback}`
    : fallback;
  return via ? `${withAirline}, via ${via}` : withAirline;
};

export const getJourneyRouteSummary = (flightItems = []) =>
  groupFlightsIntoJourneys(flightItems.filter(isFlightItineraryItem))
    .map((journey) => getJourneyRouteLabel(journey))
    .filter(Boolean)
    .join(', ');

export const parseCompositeItineraryId = (id) => {
  if (id == null) {
    return null;
  }

  const idString = String(id);
  if (!idString.includes('-')) {
    return null;
  }

  const parts = idString
    .split('-')
    .map((part) => Number.parseInt(part, 10))
    .filter((part) => Number.isInteger(part));

  return parts.length > 1 ? parts : null;
};

export const resolveFlightJourneyItems = (item, itinerary = []) => {
  if (item?._flightJourney?.items?.length) {
    return item._flightJourney.items;
  }

  const compositeIds = parseCompositeItineraryId(item?.id);
  if (compositeIds?.length) {
    return compositeIds.map((id) =>
      itinerary.find((entry) => String(entry.id) === String(id)) || { id }
    );
  }

  if (item?.id != null) {
    return [item];
  }

  return [];
};

export const buildFlightLegUpdatePayload = (
  basePayload,
  leg,
  existingItem = {},
  { isFirstLeg = false } = {}
) => {
  const transportation_data = buildTransportationDataFromLegs(
    leg ? [leg] : [],
    existingItem.transportation_data || {}
  );

  const flightName =
    leg?.airline && leg?.flight_number
      ? `${leg.airline}${leg.flight_number}`.trim()
      : existingItem.name;

  return {
    name: isFirstLeg ? basePayload.name || flightName || existingItem.name : flightName || existingItem.name,
    location: basePayload.location ?? existingItem.location ?? '',
    google_map_link: basePayload.google_map_link ?? existingItem.google_map_link ?? '',
    category: basePayload.category ?? existingItem.category ?? 'transport',
    start_datetime: leg?.departure_time || basePayload.start_datetime || existingItem.start_datetime,
    end_datetime: leg?.arrival_time || basePayload.end_datetime || existingItem.end_datetime,
    description: basePayload.description ?? existingItem.description ?? '',
    image_url: isFirstLeg ? (basePayload.image_url ?? existingItem.image_url ?? null) : existingItem.image_url,
    cost: isFirstLeg ? basePayload.cost : existingItem.cost,
    cost_currency: basePayload.cost_currency ?? existingItem.cost_currency ?? 'USD',
    transportation_type: 'flight',
    transportation_data,
  };
};

export const buildTransportationDataFromLegs = (legs = [], existingData = {}) => {
  const normalizedLegs = legs.filter((leg) =>
    leg.flight_number ||
    leg.departure_airport ||
    leg.arrival_airport ||
    leg.departure_time ||
    leg.arrival_time
  );

  if (!normalizedLegs.length) {
    return existingData;
  }

  const firstLeg = normalizedLegs[0];
  const lastLeg = normalizedLegs[normalizedLegs.length - 1];

  return {
    ...existingData,
    legs: normalizedLegs,
    airline: firstLeg.airline || existingData.airline || '',
    flight_number: firstLeg.flight_number || existingData.flight_number || '',
    departure_airport: firstLeg.departure_airport || '',
    arrival_airport: lastLeg.arrival_airport || '',
    departure_time: firstLeg.departure_time || '',
    arrival_time: lastLeg.arrival_time || '',
  };
};

export const legsToInitialFormLegs = (item) => {
  const rawLegs = getFlightLegsFromItem(item);
  return rawLegs.map((leg, index, allLegs) => enrichFlightLegForForm(leg, item, index, allLegs));
};

/**
 * When a flight journey is stored as multiple separate itinerary items (one per
 * leg), editing a single item would only expose that item's leg. This resolves
 * the full journey the given flight belongs to and, when it spans more than one
 * item, returns a synthetic composite item carrying every leg so the edit modal
 * can display and update the whole journey. Returns null when the flight is a
 * standalone item (it already holds all of its legs).
 */
export const buildFlightJourneyEditItem = (item, itinerary = []) => {
  if (!item || !isFlightItineraryItem(item)) {
    return null;
  }

  const flights = (itinerary || []).filter(isFlightItineraryItem);
  const journeys = groupFlightsIntoJourneys(flights);
  const journey = journeys.find((entry) =>
    entry.items.some((journeyItem) => String(journeyItem.id) === String(item.id))
  );

  if (!journey || journey.items.length <= 1) {
    return null;
  }

  const first = journey.items[0];
  const last = journey.items[journey.items.length - 1];
  const mergedLegs = journey.items.flatMap((journeyItem) => getFlightLegsFromItem(journeyItem));

  return {
    ...first,
    id: journey.id,
    _flightJourney: { items: journey.items },
    start_datetime: journey.start_datetime || first.start_datetime,
    end_datetime: journey.end_datetime || last.end_datetime,
    transportation_type: 'flight',
    transportation_data: {
      ...(first.transportation_data || {}),
      legs: mergedLegs,
    },
  };
};
